import { computed, Directive, input } from '@angular/core';

import { buttonEnumInputTransformer, ButtonEnumInputType } from './button-common-transformers';

export enum CommonButtonFilledSize {
	XS = 'XS',
	S = 'S',
	M = 'M',
	L = 'L',
}

export enum CommonButtonFilledState {
	DEFAULT = 'DEFAULT',
	SELECTED = 'SELECTED',
	DISABLED = 'DISABLED',
}

export enum CommonButtonFilledColor {
	DEFAULT = 'DEFAULT',
	PRIMARY = 'PRIMARY',
}

const CLASSES_BY_SIZE_MAP: Record<CommonButtonFilledSize, string[]> = {
	[CommonButtonFilledSize.XS]: ['br-0t', 'p-0h-tb', 'p-1-l', 'p-1h-r'],
	[CommonButtonFilledSize.S]: ['br-1', 'p-0t-tb', 'p-1h-rl'],
	[CommonButtonFilledSize.M]: ['br-1', 'p-1-tb', 'p-2-rl'],
	[CommonButtonFilledSize.L]: ['br-1', 'p-1-tb', 'p-3-rl'],
};

const CLASSES_BY_COLOR_AND_STATE_MAP: Record<
	CommonButtonFilledColor,
	Record<CommonButtonFilledState, string[]>
> = {
	[CommonButtonFilledColor.DEFAULT]: {
		[CommonButtonFilledState.DEFAULT]: [
			'bg-260',
			'fc-500',
			'bg-standard-hover-on-hover-with-bg-260',
			'fc-800-on-hover',
		],
		[CommonButtonFilledState.SELECTED]: ['fc-800', 'bg-primary-20'],
		[CommonButtonFilledState.DISABLED]: ['bg-270', 'fc-200'],
	},
	[CommonButtonFilledColor.PRIMARY]: {
		[CommonButtonFilledState.DEFAULT]: ['bg-primary', 'fc-white', 'bg-primary-700-on-hover'],
		[CommonButtonFilledState.SELECTED]: ['fc-800-as-bg', 'bg-190-as-fc'],
		[CommonButtonFilledState.DISABLED]: ['fc-200-as-bg', 'bg-190-as-fc'],
	},
};

@Directive({
	selector: '[commonButtonFilled]',
	standalone: true,
	host: {
		class: 'fl fl-ai-c fl-jc-fs fl-gap-0t',
		'[class]': '$classes()',
		'[attr.data-size]': '$size()',
		'[attr.data-state]': '$state()',
		'[attr.data-color]': '$color()',
		'[attr.disabled]': '$disabled()',
	},
})
export class CommonButtonFilledDirective {
	public readonly $size = input<
		CommonButtonFilledSize,
		ButtonEnumInputType<CommonButtonFilledSize>
	>(CommonButtonFilledSize.M, {
		alias: 'size',
		transform: (value) =>
			buttonEnumInputTransformer(
				value,
				Object.values(CommonButtonFilledSize),
				CommonButtonFilledSize.M
			),
	});

	public readonly $color = input<
		CommonButtonFilledColor,
		ButtonEnumInputType<CommonButtonFilledColor>
	>(CommonButtonFilledColor.DEFAULT, {
		alias: 'color',
		transform: (value) =>
			buttonEnumInputTransformer(
				value,
				Object.values(CommonButtonFilledColor),
				CommonButtonFilledColor.DEFAULT
			),
	});

	public readonly $state = input<
		CommonButtonFilledState,
		ButtonEnumInputType<CommonButtonFilledState>
	>(CommonButtonFilledState.DEFAULT, {
		alias: 'state',
		transform: (value) =>
			buttonEnumInputTransformer(
				value,
				Object.values(CommonButtonFilledState),
				CommonButtonFilledState.DEFAULT
			),
	});

	protected readonly $classes = computed(() => {
		const size = this.$size();
		const state = this.$state();
		const color = this.$color();

		const classesBySize = CLASSES_BY_SIZE_MAP[size] ?? [];
		const classesByState = CLASSES_BY_COLOR_AND_STATE_MAP[color][state] ?? [];

		const classes = [...classesBySize, ...classesByState];

		return classes.join(' ');
	});

	protected readonly $disabled = computed(() => {
		const state = this.$state();
		return state === CommonButtonFilledState.DISABLED;
	});
}
